/**
 * @file src/schemas/identity.schema.js
 * @description 
 * Authoritative Machine-Contracts for Identity (Service #2, #3, #5).
 * 
 * Standards:
 * - 1.1: Zero hardcoded human strings (uses machine-codes).
 * - 8.1: Structural Integrity (Strict Zod Typing).
 * - 13.4: Graceful Degradation (Safe Fallbacks).
 */

import { z } from 'zod';
import { RECORD_EXTENSIONS, RECORD_CORE } from '@moksy/common/src/masterSchema/personStore.js';
import { OWNER_ROLES } from '@moksy/common/src/permissions/ownerRoles.js';

// =========================================================
// 🧱 ATOMIC DEFINITIONS (Standard 18: Reusability)
// =========================================================

const Email = z.string().email("AUTH.ERR_INVALID_EMAIL").trim().toLowerCase();
const LoginPassword = z.string().min(1, "AUTH.ERR_PASSWORD_REQUIRED");
const RegistrationPassword = z.string()
  .min(12, "AUTH.ERR_PASSWORD_SHORT")
  .regex(/[A-Z]/, "AUTH.ERR_PASSWORD_NEEDS_UPPER")
  .regex(/[a-z]/, "AUTH.ERR_PASSWORD_NEEDS_LOWER")
  .regex(/[0-9]/, "AUTH.ERR_PASSWORD_NEEDS_NUMBER")
  .regex(/[^A-Za-z0-9]/, "AUTH.ERR_PASSWORD_NEEDS_SPECIAL");
const Username = z.string().min(3, "AUTH.ERR_USERNAME_SHORT").max(30).trim();

// Standard 13.4: Resilience gate for roles
//
// RF-075: this used to recognise only 'user', 'admin', 'super_admin' —
// every OTHER real owner-group role (support, manager, finance,
// content_moderator, reviewer_coordinator, ...) fell through `.catch('user')`
// and was silently rewritten to 'user' on every single profile fetch. Not a
// parse failure anybody could see — the request succeeded, the raw response
// genuinely carried `role: "support"`, and this schema quietly replaced it
// before the rest of the app ever saw it. A real support-desk account
// landing in the Customer persona and getting refused as "signed in as a
// member" traced all the way back to this one line: the persona-settling
// logic downstream was correctly reading `user.role`, but `user.role` was
// already wrong by the time it got there.
//
// Same shape, same fix, as WorkerRole/AssignedRoles below — a client-side
// copy of the role list is always one deploy behind, so the answer is the
// same: use the real list (OWNER_ROLES, the single source of truth this
// repo already has for "every role an owner-group account can hold"), not a
// hand-picked three.
const Role = z.enum(['user', ...OWNER_ROLES]).catch('user');

// Worker roles that can be assigned alongside the primary role.
//
// `reviewer` was missing, and the cost was total: an accredited reviewer's
// profile carries `assignedRoles: ["reviewer"]`, so `UserSchema.parse` threw,
// `AuthOrchestrator.hydrateSession` caught it as a failed session and called
// `terminateSession` — which clears storage. The login itself was fine (200,
// valid token, /profile 200); the app wiped the session a moment later and
// bounced to /login with nothing stored and nothing in the console, because
// the throw was caught. No reviewer could sign in to the dashboard.
const WorkerRole = z.enum([
  'user', 'admin', 'super_admin', 'developer', 'designer', 'content_writer',
  'reviewer'
]);

/**
 * An assigned role this client does not recognise DROPS OUT. It does not
 * invalidate the profile.
 *
 * The server grants roles; this list is a copy of what the server knows, and a
 * copy is always one deploy behind. Parsing strictly means the next role
 * anybody adds server-side signs out every user who holds it — silently, and
 * looking exactly like a broken password. That is not a hypothetical: it is
 * what `reviewer` just did.
 *
 * Same reasoning as the row-level `.catch` on the user LIST below: one bad
 * element must cost that element, never the whole payload.
 */
const AssignedRoles = z.array(WorkerRole.catch(null))
  .optional()
  .default([])
  .transform((roles) => (roles || []).filter(Boolean));

// =========================================================
// 👤 PROFILE DOMAIN (Service #3, #5)
// =========================================================

/**
 * 🛡️ USER SCHEMA
 * Logic: Normalizes Mongo ObjectIDs or legacy Integers into strict strings.
 */
export const UserSchema = z.object({
  id: z.string().or(z.number()).transform(String),
  username: Username,
  email: Email,
  role: Role,
  assignedRoles: AssignedRoles,
  profilePictureUrl: z.string().optional().nullable(),
  avatarUrl: z.string().optional().nullable(),
  pictureUrl: z.string().optional().nullable(),
  avatarIcon: z.string().optional().nullable(),
  createdAt: z.string().datetime().optional().nullable(),
  updatedAt: z.string().datetime().optional().nullable()
}).passthrough();

/**
 * 📋 COLLECTION SCHEMA
 * Standard 13.4: degrades gracefully instead of throwing on list failure.
 *
 * Per-ROW, not per-list. This was `z.array(UserSchema).catch([])`, and the
 * combination is a trap: zod fails the whole array if any single element fails,
 * and `.catch([])` then turns that into an empty list with no error anywhere.
 * One user with a two-character username or a malformed email — both of which
 * exist in real data — made the admin registry render "no users" for every
 * super admin, silently, while several hundred perfectly valid records sat
 * behind it.
 *
 * One bad row should cost you that row. It should never cost you the page.
 */
/**
 * ⚠️ THREE SHAPES, AND FAILING TO MATCH ONE IS SILENT.
 *
 * The users-service list endpoint has returned all of these:
 *   - a bare array                      (original)
 *   - { users: [...] }                  (an older gateway wrapper)
 *   - { items, page, limit, total, ... } (current — paginated)
 *
 * `.catch([])` below means an unrecognised shape is not an error, it is an
 * EMPTY LIST. That is how the Identity Manager came to report "Total
 * registered 0 / No matches" on a platform with users, while the stats page
 * beside it correctly said 13 — the pagination envelope was added and this
 * preprocess still only knew the first two shapes.
 *
 * Accepting all three keeps the two services independently deployable, which
 * matters more here than picking one: a dashboard and a users-service are not
 * released together.
 */
export const UserListSchema = z.preprocess(
  (input) => {
    if (Array.isArray(input)) return input;
    if (Array.isArray(input?.items)) return input.items;
    if (Array.isArray(input?.users)) return input.users;
    return [];
  },
  z.array(UserSchema.catch(null)).catch([]).transform((rows) => rows.filter(Boolean))
);

export const OrgProfileSchema = z.object({
  id: z.string().or(z.number()).transform(String),
  name: z.string(),
  slug: z.string().optional().default(''),
  description: z.string().optional().default(''),
  website: z.string().optional().default(''),
  logoUrl: z.string().optional().default(''),
  profile: z.object({
    headline: z.string().optional().default(''),
    shortBio: z.string().optional().default(''),
    industry: z.string().optional().default(''),
    sector: z.string().optional().default(''),
    organisationType: z.string().optional().default(''),
    size: z.string().optional().default(''),
    geography: z.string().optional().default(''),
    foundedYear: z.number().nullable().optional(),
    supportEmail: z.string().optional().default(''),
    contactEmail: z.string().optional().default(''),
    phone: z.string().optional().default(''),
    bannerUrl: z.string().optional().default(''),
    heroImageUrl: z.string().optional().default(''),
    socialLinks: z.record(z.string(), z.string()).optional().default({})
  }).optional().default({}),
  settings: z.object({
    privacy: z.object({
      profilePublic: z.boolean().optional().default(false)
    }).optional().default({}),
    aiBilling: z.object({
      enabled: z.boolean().optional().default(false),
      billingMode: z.enum(['individual', 'shared_pool', 'hybrid']).optional().default('individual'),
      includedTokensMonthly: z.number().optional().default(0),
      overageEnabled: z.boolean().optional().default(false),
      overageRatePer1kUsd: z.number().optional().default(0),
      hardLimitMode: z.enum(['block', 'downgrade', 'allow_overage']).optional().default('block'),
      maxTokensPerRequest: z.number().optional().default(0),
      allowedModels: z.array(z.string()).optional().default([]),
      poolLabel: z.string().optional().default('')
    }).optional().default({})
  }).optional().default({}),
  services: z.array(z.any()).optional().default([]),
  products: z.array(z.any()).optional().default([]),
  solutions: z.array(z.any()).optional().default([]),
  earnings: z.record(z.string(), z.any()).optional().default({}),
  members: z.array(z.any()).optional().default([])
}).passthrough();

export const OrgCatalogEntrySchema = z.object({
  name: z.string().min(1),
  category: z.string().optional(),
  description: z.string().optional(),
  status: z.enum(['draft', 'active', 'archived']).optional(),
  visibility: z.enum(['private', 'public', 'marketplace']).optional(),
  featured: z.boolean().optional(),
  purchasable: z.boolean().optional(),
  buyLabel: z.string().optional(),
  deliveryMode: z.string().optional(),
  url: z.string().optional(),
  priceFrom: z.number().nullable().optional(),
  currency: z.string().optional(),
  tags: z.array(z.string()).optional()
}).strict();

export const OrgSettingsPatchSchema = z.object({
  privacy: z.object({
    profilePublic: z.boolean().optional()
  }).optional(),
  aiBilling: z.object({
    enabled: z.boolean().optional(),
    billingMode: z.enum(['individual', 'shared_pool', 'hybrid']).optional(),
    includedTokensMonthly: z.number().optional(),
    overageEnabled: z.boolean().optional(),
    overageRatePer1kUsd: z.number().optional(),
    hardLimitMode: z.enum(['block', 'downgrade', 'allow_overage']).optional(),
    maxTokensPerRequest: z.number().optional(),
    allowedModels: z.array(z.string()).optional(),
    poolLabel: z.string().optional()
  }).optional()
}).strict();

export const OrgEarningsPatchSchema = z.object({
  currency: z.string().optional(),
  totalRevenue: z.number().nullable().optional(),
  monthlyRevenue: z.number().nullable().optional(),
  annualRevenue: z.number().nullable().optional(),
  rangeLabel: z.string().optional(),
  isPublic: z.boolean().optional(),
  lastUpdatedAt: z.string().optional()
}).strict();

export const OrgPublicProfileSchema = z.object({
  id: z.string().or(z.number()).transform(String),
  type: z.literal('organisation'),
  name: z.string(),
  slug: z.string(),
  description: z.string().optional().default(''),
  website: z.string().optional().default(''),
  logoUrl: z.string().optional().default(''),
  visibility: z.enum(['public', 'limited']).optional().default('limited'),
  profile: z.record(z.string(), z.any()).optional().default({}),
  stats: z.record(z.string(), z.any()).optional().default({}),
  members: z.array(z.any()).optional().default([]),
  services: z.array(z.any()).optional().default([]),
  products: z.array(z.any()).optional().default([]),
  solutions: z.array(z.any()).optional().default([]),
  projects: z.array(z.any()).optional().default([]),
  earnings: z.any().nullable().optional()
}).passthrough();

export const UserPublicProfileSchema = z.object({
  id: z.string().or(z.number()).transform(String),
  type: z.literal('user'),
  username: z.string(),
  role: z.string().optional().default('user'),
  about: z.record(z.string(), z.any()).optional().default({}),
  workStatus: z.record(z.string(), z.any()).optional().default({}),
  profilePublic: z.boolean().optional().default(false),
  employment: z.array(z.any()).optional().default([]),
  education: z.array(z.any()).optional().default([]),
  skills: z.array(z.any()).optional().default([]),
  certifications: z.array(z.any()).optional().default([])
}).passthrough();

/**
 * 📝 PROFILE UPDATE CONTRACT
 */
export const ProfileUpdateSchema = z.object({
  username: Username.optional(),
  email: Email.optional(),
  password: RegistrationPassword.optional().or(z.literal('')),
  currentPassword: z.string().optional()
});

export const AboutUpdateSchema = z.object({
  title: z.string().max(120).optional(),
  firstName: z.string().max(80).optional(),
  lastName: z.string().max(80).optional(),
  shortBio: z.string().max(500).optional(),
  photoUrl: z.string().max(2000000).optional(),
  avatarIcon: z.string().max(8).optional(),
  // Mirrors users-service ProfileContract.AboutUpdateSchema. Both are
  // `.strict()`, so a field missing HERE is rejected before it ever
  // reaches the service — which is why this has to move in step.
  links: z.object({
    linkedin: z.string().max(500).optional(),
    github:   z.string().max(500).optional(),
    cv:       z.string().max(500).optional(),
    website:  z.string().max(500).optional()
  }).strict().optional()
}).strict();

export const BillingUpdateSchema = z.object({
  line1: z.string().max(160),
  line2: z.string().max(160).optional(),
  city: z.string().max(80),
  state: z.string().max(80).optional(),
  postalCode: z.string().max(30),
  country: z.string().max(80),
  // Tax / VAT ID (e.g. GB 123456789). Optional — only present for B2B
  // invoices. The users-service schema (User.billing) accepts it as
  // a free-form string; the payments service uses it to populate
  // invoice tax_id_collection for Stripe.
  taxId: z.string().max(40).optional()
}).strict();

export const WorkStatusPatchSchema = z.object({
  acceptingRequests: z.boolean()
}).strict();

const ConsentSchema = z.object({
  status: z.enum(['accepted', 'declined', 'customized']).optional(),
  version: z.string().min(1).optional(),
  source: z.enum(['banner', 'settings', 'migration']).optional(),
  updatedAt: z.string().optional(),
  categories: z.object({
    essential: z.literal(true).optional(),
    functional: z.boolean().optional(),
    analytics: z.boolean().optional(),
    marketing: z.boolean().optional()
  }).optional()
}).strict();

export const SettingsPatchSchema = z.object({
  notifications: z.object({
    email: z.boolean().optional(),
    inApp: z.boolean().optional()
  }).optional(),
  privacy: z.object({
    profilePublic: z.boolean().optional()
  }).optional(),
  consent: ConsentSchema.optional()
}).strict();

/**
 * Each career record — its core (company, title, dates, credential ID…) and
 * the master schema's extensions (role key, rung, level, skills…) — read from
 * the one declaration the users service validates against and the /profile
 * forms are drawn from (common/masterSchema/personStore.js RECORD_CORE and
 * RECORD_EXTENSIONS). Loose here on purpose: the service holds the real
 * validator, and a stricter copy in the browser is how the two come to
 * disagree about what is valid. This only has to let the declared keys
 * through a `.strict()` object, and insist on what a record cannot be
 * without. The core used to be typed out here by hand — the fourth copy of a
 * list whose form asked for three of its fields (GC-032/033/034).
 */
const loose = (f) => {
  if (f.required) return z.string().trim().min(f.minLength || 1).max(f.max || 1000);
  if (f.type === 'enum') return z.enum(f.values).nullable().optional();
  if (f.type === 'int' || f.type === 'number') return z.number().nullable().optional();
  if (f.type === 'list') return z.array(z.string()).optional();
  if (f.type === 'bool') return z.boolean().nullable().optional();
  return z.string().max(f.max || 1000).nullable().optional();
};
const recordSchema = (record) => z.object(Object.fromEntries(
  [...(RECORD_CORE[record] || []), ...(RECORD_EXTENSIONS[record] || [])].map((f) => [f.key, loose(f)])
)).strict();

export const EducationCreateSchema = recordSchema('education');
export const EmploymentCreateSchema = recordSchema('employment');
export const SkillCreateSchema = recordSchema('skills');
export const CertificationCreateSchema = recordSchema('certifications');

export const InviteSendSchema = z.object({
  email: z.string().email(),
  message: z.string().max(1000).optional(),
  projectId: z.string().optional(),
  role: z.enum(['viewer', 'editor']).optional()
}).strict();

export const SecurityQuestionsUpdateSchema = z.object({
  questions: z.array(z.object({
    question: z.string().min(2),
    // Empty keeps the saved answer ("Saved — type to replace").
    answer: z.union([z.string().min(2), z.literal('')])
  })).min(1)
}).strict();

// =========================================================
// 🔐 AUTHENTICATION GATES (Service #2)
// =========================================================

/**
 * @class AuthValidator
 * @description Orchestrates the validation gates for authentication flows.
 */
// ⚠️ Declared, not merely tolerated.
//
// These contracts are applied with `.parse()`, which returns ONLY the declared
// keys — so an undeclared captcha token is dropped here, in the browser,
// before the request is built. The server would then see no token and reject
// every attempt the moment an operator enabled the captcha, while the network
// tab showed a request that never contained one.
//
// The same trap exists again on the gateway (validate() reassigns req.body
// from the parsed result). Both layers have to know about the field.
const CaptchaToken = z.string().min(1).optional();

class AuthValidator {
  /**
   * 🔑 LOGIN GATE
   */
  static get loginContract() {
    return z.object({
      email: Email,
      password: LoginPassword,
      captchaToken: CaptchaToken
    });
  }

  /**
   * 🆔 PROVISIONING GATE
   */
  static get registrationContract() {
    return z.object({
      username: Username,
      email: Email,
      password: RegistrationPassword,
      role: z.literal('user').default('user'),
      // GC-020 · The person's name. Required by the form (it is the first
      // field), optional in the contract so the other callers of
      // `identity.provision` — the admin console, the tests — are unaffected.
      fullName: z.string().trim().min(1, 'AUTH.ERR_NAME_REQUIRED').max(120).optional(),
      // GC-017 · Why they are here (P33.capacity.intent). The server holds
      // the authoritative enum; this only has to not mangle it.
      capacity: z.string().trim().min(1).max(40).optional(),
      // GC-018 · The 18+ / terms confirmation. The VERSION is stamped
      // server-side and deliberately not sent from here.
      ageConfirmed: z.boolean().optional(),
      // A referral code from a shared /register?ref= link. Optional, and the
      // gateway acts on it (progression records the referral); the reward is
      // paid on the referee's paid conversion, never on signup.
      referralCode: z.string().trim().min(4).max(64).optional(),
      captchaToken: CaptchaToken
    });
  }

  /**
   * 🧪 EXECUTORS
   * Standard 1.5: Functional boundary for safe execution.
   */
  static login(data) {
    return this.loginContract.parse(data);
  }

  static registration(data) {
    return this.registrationContract.parse(data);
  }
}

/**
 * 🛡️ AUTHORITATIVE EXPORT
 * Logic: Resolves the SyntaxError in identity.js by providing the validator class.
 */
export { AuthValidator as authValidator };
